import { Helmet } from 'react-helmet-async'
import { Package, RefreshCw, Clock, Shield, CheckCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const DeliveryRefund = () => {
  const { language } = useLanguage()

  const content = {
    tr: {
      title: 'Teslimat ve İade Şartları',
      lastUpdated: 'Son Güncelleme: 6 Kasım 2024',
      sections: [
        {
          icon: Package,
          title: 'Teslimat',
          content: `Dijital ürünlerimiz ve online eğitimlerimiz anında teslim edilir. Satın alma işleminizi tamamladıktan hemen sonra:

• Erişim bilgileriniz e-posta adresinize gönderilir
• Hesabınıza giriş yaparak ürünlere erişebilirsiniz
• Video eğitimler ve dijital içerikler anında indirilebilir veya izlenebilir durumda olur
• Satın alma işleminizden sonra 24 saat içinde tüm erişim bilgileri tarafınıza iletilecektir`,
        },
        {
          icon: RefreshCw,
          title: 'İade Politikası',
          content: `Dijital ürünlerin doğası gereği, 6502 sayılı Tüketicinin Korunması Hakkında Kanun'un 15/g maddesi uyarınca, tüketicinin onayı ile anında ifa edilen veya hizmet unsuru ağırlıklı olan dijital içerikler için cayma hakkı uygulanmamaktadır.

Ancak, aşağıdaki durumlarda iade talebiniz değerlendirilecektir:

• Ürün veya hizmet teknik olarak erişilebilir değilse
• Satın aldığınız ürün ile teslim edilen ürün farklıysa
• Ürün açıklamasında belirtilen özelliklere sahip değilse
• Erişim sorunları yaşanıyor ve 7 iş günü içinde çözülememişse

İade talepleriniz 14 gün içinde değerlendirilir ve gerekli durumlarda ödeme iadeniz yapılır.`,
        },
        {
          icon: Clock,
          title: 'İade Süreci',
          content: `İade talebiniz için:

1. İade talebinizi info@thinklyze.com adresine e-posta ile gönderin
2. Satın alma bilgilerinizi (sipariş numarası, tarih, ürün adı) belirtin
3. İade nedeninizi açıklayın
4. Gerekli durumlarda destek kanıtları ekleyin (ekran görüntüleri, hata mesajları)

İade talebiniz incelendikten sonra 14 iş günü içinde size dönüş yapılacaktır.`,
        },
        {
          icon: Shield,
          title: 'Özel Durumlar',
          content: `Aşağıdaki durumlarda iade kabul edilmez:

• Ürünü tamamen kullanmış veya tamamlamış olmanız
• Kişisel kullanım amacı dışında kullanım
• Satın alma işleminden sonra 30 günden fazla süre geçmiş olması
• Ürünün demo veya ücretsiz versiyonunu önceden test etmiş olmanız
• Sistem gereksinimlerini karşılamayan cihazlardan erişim sorunları`,
        },
        {
          icon: CheckCircle,
          title: 'Ödeme İadesi',
          content: `İade talebiniz onaylandığında:

• Ödeme iadeniz, ödeme yönteminize bağlı olarak 7-14 iş günü içinde yapılır
• İade tutarı, ödediğiniz tutarın tamamıdır
• İade işlemi için ekstra ücret talep edilmez
• İade durumunuz e-posta ile bilgilendirileceksiniz`,
        },
      ],
    },
    en: {
      title: 'Delivery and Refund Terms',
      lastUpdated: 'Last Updated: November 6, 2024',
      sections: [
        {
          icon: Package,
          title: 'Delivery',
          content: `Our digital products and online training are delivered instantly. Immediately after completing your purchase:

• Access information is sent to your email address
• You can access products by logging into your account
• Video training and digital content become instantly downloadable or viewable
• All access information will be sent to you within 24 hours after your purchase`,
        },
        {
          icon: RefreshCw,
          title: 'Refund Policy',
          content: `Due to the nature of digital products, in accordance with Article 15/g of Law No. 6502 on the Protection of Consumers, the right of withdrawal does not apply to digital content that is fulfilled instantly with the consumer's consent or where the service element is predominant.

However, refund requests will be evaluated in the following cases:

• If the product or service is technically inaccessible
• If the product delivered differs from the one purchased
• If the product does not have the features specified in the product description
• If access issues occur and cannot be resolved within 7 business days

Your refund requests are evaluated within 14 days and refunds are made when necessary.`,
        },
        {
          icon: Clock,
          title: 'Refund Process',
          content: `For your refund request:

1. Send your refund request via email to info@thinklyze.com
2. Specify your purchase information (order number, date, product name)
3. Explain the reason for the refund
4. Add supporting evidence if necessary (screenshots, error messages)

You will be contacted within 14 business days after your refund request is reviewed.`,
        },
        {
          icon: Shield,
          title: 'Special Cases',
          content: `Refunds are not accepted in the following cases:

• Having fully used or completed the product
• Use outside of personal use purposes
• More than 30 days have passed since the purchase
• Having previously tested the demo or free version of the product
• Access issues from devices that do not meet system requirements`,
        },
        {
          icon: CheckCircle,
          title: 'Payment Refund',
          content: `When your refund request is approved:

• Your payment refund will be made within 7-14 business days depending on your payment method
• The refund amount is the full amount you paid
• No additional fee is charged for the refund process
• You will be notified of your refund status via email`,
        },
      ],
    },
  }

  const pageContent = content[language as 'tr' | 'en' | 'ru'] || content['en']

  return (
    <>
      <Helmet>
        <title>{pageContent.title} - Thinklyze</title>
        <meta name="description" content={language === 'tr' ? 'Dijital ürünler ve online eğitimler için teslimat ve iade şartları. Thinklyze teslimat ve iade politikası.' : 'Delivery and refund terms for digital products and online training. Thinklyze delivery and refund policy.'} />
        <link rel="canonical" href="https://thinklyze.com/delivery-refund" />
        <link rel="alternate" hreflang="tr" href="https://thinklyze.com/delivery-refund" />
        <link rel="alternate" hreflang="en" href="https://thinklyze.com/delivery-refund?lang=en" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pt-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {pageContent.title}
            </h1>
            <p className="text-gray-600">{pageContent.lastUpdated}</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {pageContent.sections.map((section, index) => {
              const Icon = section.icon
              return (
                <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 pt-2">
                      {section.title}
                    </h2>
                  </div>
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line ml-16">
                    {section.content}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Contact Info */}
          <div className="mt-12 bg-blue-50 rounded-xl p-8 border border-blue-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'İletişim' : 'Contact'}
            </h3>
            <p className="text-gray-700 mb-4">
              {language === 'tr'
                ? 'İade veya teslimat konusunda sorularınız için:'
                : 'For questions about refunds or delivery:'}
            </p>
            <p className="text-gray-900 font-medium">info@thinklyze.com</p>
          </div>
        </div>
      </div>
    </>
  )
}

export default DeliveryRefund















